import { addTicket, selectActiveTicket, updateTicket } from '@/redux/data/dataSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TagsOutlined } from '@ant-design/icons';
import { Modal, Select } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import TableSelectorModal from './TableSelectorModal';

const categories = [
  {
    label: 'Dine In',
    value: 'IN',
  },
  {
    label: 'Takeaway',
    value: 'OUT',
  },
  {
    label: 'Delivery',
    value: 'DELIVERY',
  },
];

const SelectCategoryModal = ({ open, onCancel }: { open: boolean; onCancel: () => void }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [showTableSelector, setShowTableSelector] = useState(false);
  const activeTicket = useAppSelector(selectActiveTicket);

  const handleChangeCategory = (value: 'IN' | 'OUT' | 'DELIVERY') => {
    if (value === 'IN') {
      // For dine-in, show table selector first
      setShowTableSelector(true);
    } else {
      // For takeaway/delivery, create ticket directly
      if (activeTicket) {
        dispatch(updateTicket({ ...activeTicket, category: value, tableId: null }));
      } else {
        dispatch(
          addTicket({
            ID: uuidv4(),
            status: 'ACTIVE',
            category: value,
            type: 'PAY',
            createdAt: dayjs().toISOString(),
            items: [],
            tableId: null,
            savedToBackend: false,
          })
        );
      }
      onCancel();
    }
  };

  const handleTableSelect = (tableId: string) => {
    if (activeTicket) {
      dispatch(updateTicket({ ...activeTicket, category: 'IN', tableId }));
    } else {
      dispatch(
        addTicket({
          ID: uuidv4(),
          status: 'ACTIVE',
          category: 'IN',
          type: 'PAY',
          createdAt: dayjs().toISOString(),
          items: [],
          tableId,
          savedToBackend: false,
        })
      );
    }
    onCancel();
    setShowTableSelector(false);
  };

  const handleTableCancel = () => {
    setShowTableSelector(false);
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      destroyOnClose
      title={t('common:select_category')}
    >
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <TagsOutlined className="text-[80px] text-gray-400" />
        <div className="flex flex-col items-center gap-1.5">
          <span className="text-sm font-semibold text-gray-500">
            {t('common:select_category_title')}
          </span>
          <span className="text-sm font-semibold text-gray-500">
            {t('common:select_category_desc')}
          </span>
        </div>
        <Select
          options={categories}
          onChange={handleChangeCategory}
          className="w-52"
          size="large"
          placeholder={t('common:select_category')}
        />
      </div>

      <TableSelectorModal
        open={showTableSelector}
        onSelect={handleTableSelect}
        onCancel={handleTableCancel}
      />
    </Modal>
  );
};

export default SelectCategoryModal;
